import * as React from 'react';
import { useState, useEffect } from 'react';
import { useColorScheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Grid2 as Grid,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  Checkbox,
  FormGroup,
  Select,
  MenuItem,
  FormControl,
  Avatar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

import { authFetch } from '../authFetch'; 

export default function SettingsTab() {
  const { mode, setMode } = useColorScheme();
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // State definitions (no changes)
  const [emailNotifications, setEmailNotifications] = useState({
    hazardUpdates: true,
    statusChanges: true,
    systemMaintenance: true,
  });

  const [userInfo, setUserInfo] = useState({
    username: '',
    email: '',
    profilePictureUrl: null,
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [alert, setAlert] = useState({ show: false, type: 'success', message: '' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchUserProfile();
  }, []);

  // All API call functions using authFetch remain the same
  const fetchUserProfile = async () => {
    try {
      const response = await authFetch('/api/user/profile/');
      const data = await response.json();
      
      setUserInfo({
        username: data.username,
        email: data.email,
        profilePictureUrl: data.profile_picture,
      });

      // --- BULLETPROOF FIX START ---
      // If data.settings is null, use an empty object {}
      // This guarantees accessing properties won't crash
      const userSettings = data.settings || {}; 

      setEmailNotifications({
        hazardUpdates: userSettings.hazard_updates ?? true, // Default to true if missing
        statusChanges: userSettings.status_changes ?? true,
        systemMaintenance: userSettings.system_maintenance ?? true,
      });
      // --- BULLETPROOF FIX END ---

    } catch (error) {
      console.error(error); // Log the full error to console
      showAlert('error', 'Failed to load user profile. ' + error.message);
    } finally {
      setInitialLoad(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
  };
  const handleNotificationChange = (event) => setEmailNotifications({ ...emailNotifications, [event.target.name]: event.target.checked });
  const handleUserInfoChange = (field, value) => {
    setUserInfo({ ...userInfo, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: null });
  };
  const handlePasswordChange = (field, value) => {
    setPasswordData({ ...passwordData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: null });
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return showAlert('error', 'Please select an image file');
    if (file.size > 5 * 1024 * 1024) return showAlert('error', 'File size must be less than 5MB');
    
    const formData = new FormData();
    formData.append('profile_picture', file);
    setLoading(true);

    try {
      const response = await authFetch('/api/user/profile-picture/upload/', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      setUserInfo({ ...userInfo, profilePictureUrl: data.profile_picture_url });
      showAlert('success', 'Profile picture updated successfully');
      // Notify sidebar components to re-fetch the updated profile
      window.dispatchEvent(new CustomEvent('profileUpdated'));
    } catch (error) {
      showAlert('error', error.message || 'Failed to upload profile picture.');
    } finally {
      setLoading(false);
      event.target.value = null;
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setErrors({});
    try {
      const response = await authFetch('/api/user/profile/', {
        method: 'PATCH',
        body: JSON.stringify({
          username: userInfo.username,
          email: userInfo.email,
        }),
      });
      await response.json();
      showAlert('success', 'Profile updated successfully');
      // Notify sidebar components to re-fetch the updated profile
      window.dispatchEvent(new CustomEvent('profileUpdated'));
    } catch (error) {
      setErrors(error.data || {});
      showAlert('error', error.message || 'Please correct the errors below.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setLoading(true);
    setErrors({});
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setErrors({ confirmPassword: 'Passwords do not match' });
      setLoading(false);
      return;
    }

    try {
      const response = await authFetch('/api/user/password/change/', {
        method: 'POST',
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword,
          confirm_password: passwordData.confirmPassword,
        }),
      });
      await response.json();
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showAlert('success', 'Password changed successfully');
    } catch (error) {
      setErrors(error.data || {});
      showAlert('error', error.message || 'Please correct the errors below.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotifications = async () => {
    setLoading(true);
    try {
      const response = await authFetch('/api/user/settings/', {
        method: 'PUT',
        body: JSON.stringify({
          hazard_updates: emailNotifications.hazardUpdates,
          status_changes: emailNotifications.statusChanges,
          system_maintenance: emailNotifications.systemMaintenance,
        }),
      });
      await response.json();
      showAlert('success', 'Notification settings saved successfully');
    } catch (error) {
      showAlert('error', error.message || 'Failed to save notification settings.');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoad) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1200px' }, mx: 'auto', p: 2 }}>
      <Typography component="h1" variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
        Settings
      </Typography>

      {alert.show && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert({ ...alert, show: false })}>
          {alert.message}
        </Alert>
      )}

      {/* Accordion sections... */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="h6">Appearance</Typography></AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <Box><Typography variant="subtitle2" sx={{ mb: 2 }}>Color Mode</Typography>
              <FormControl fullWidth sx={{ maxWidth: 300 }}>
                <Select value={mode || 'system'} onChange={(event) => setMode(event.target.value)}>
                  <MenuItem value="system">System</MenuItem>
                  <MenuItem value="light">Light</MenuItem>
                  <MenuItem value="dark">Dark</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="h6">Notifications</Typography></AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <Box><Typography variant="subtitle2" sx={{ mb: 2 }}>Email Notifications</Typography>
              <FormGroup>
                <FormControlLabel control={<Checkbox checked={emailNotifications.hazardUpdates} onChange={handleNotificationChange} name="hazardUpdates" />} label="New hazard dataset updates" />
                <FormControlLabel control={<Checkbox checked={emailNotifications.statusChanges} onChange={handleNotificationChange} name="statusChanges" />} label="Status changes to moderate or high risk" />
                <FormControlLabel control={<Checkbox checked={emailNotifications.systemMaintenance} onChange={handleNotificationChange} name="systemMaintenance" />} label="System maintenance notifications" />
              </FormGroup>
            </Box>
            <Box>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveNotifications} disabled={loading}>{loading ? 'Saving...' : 'Save Notification Settings'}</Button>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="h6">Privacy</Typography></AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2">We only store essential information required for your account and to process feedback. For full details on the data we collect and how we use it, please read our{' '}<Link href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.</Typography>
        </AccordionDetails>
      </Accordion>

      {/* --- MODIFIED ACCOUNT SECTION --- */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="h6">Account</Typography></AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Profile Picture</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src={userInfo.profilePictureUrl} sx={{ width: 80, height: 80 }}>{userInfo.username ? userInfo.username.charAt(0).toUpperCase() : ''}</Avatar>
                <input accept="image/*" style={{ display: 'none' }} id="profile-picture-upload" type="file" onChange={handleProfilePictureUpload} />
                <label htmlFor="profile-picture-upload">
                  <Button variant="outlined" component="span" startIcon={<PhotoCameraIcon />} disabled={loading}>Change Picture</Button>
                </label>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {/* FIX 1: Changed grid layout for username and email */}
              <Grid xs={12} sm={4}>
                <TextField fullWidth variant="outlined" label="Username" value={userInfo.username} onChange={(e) => handleUserInfoChange('username', e.target.value)} error={!!errors.username} helperText={errors.username} disabled={loading} />
              </Grid>
              <Grid xs={12} sm={8}>
                <TextField fullWidth variant="outlined" label="Email" type="email" value={userInfo.email} onChange={(e) => handleUserInfoChange('email', e.target.value)} error={!!errors.email} helperText={errors.email} disabled={loading} />
              </Grid>
            </Grid>

            <Box>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveProfile} disabled={loading}>{loading ? 'Saving...' : 'Save Profile Changes'}</Button>
            </Box>
            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Change Password</Typography>
              <Grid container spacing={2}>
                {/* FIX 2: Added variant="outlined" to all TextFields to ensure consistent styling */}
                <Grid xs={12} sm={4}>
                  <TextField fullWidth variant="outlined" label="Current Password" type="password" value={passwordData.currentPassword} onChange={(e) => handlePasswordChange('currentPassword', e.target.value)} error={!!errors.current_password} helperText={errors.current_password} disabled={loading} />
                </Grid>
                <Grid xs={12} sm={4}>
                  <TextField fullWidth variant="outlined" label="New Password" type="password" value={passwordData.newPassword} onChange={(e) => handlePasswordChange('newPassword', e.target.value)} error={!!errors.new_password} helperText={errors.new_password} disabled={loading} />
                </Grid>
                <Grid xs={12} sm={4}>
                  <TextField fullWidth variant="outlined" label="Confirm New Password" type="password" value={passwordData.confirmPassword} onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)} error={!!errors.confirmPassword} helperText={errors.confirmPassword} disabled={loading} />
                </Grid>
              </Grid>
            </Box>
            
            <Box>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleChangePassword}
                disabled={loading || !passwordData.currentPassword || !passwordData.newPassword}
              >
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}